import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
} from 'react';

type FitTextProps = {
  /** The string to lay out. A prop rather than children so a change re-measures. */
  text: string;
  /** Size used whenever the text already fits. */
  maxFontSize: number;
  /** Never shrink past this — below it the text stops being readable. */
  minFontSize: number;
  className?: string;
  style?: CSSProperties;
};

/**
 * One line of text shrunk just enough to fit its container.
 *
 * The daily-reveal app carries the same component (`apps/daily-reveal/src/
 * components/common/FitText.tsx`) for the reveal cards. My Games renders those
 * same picks, so it needs the same treatment; the two apps share no runtime
 * package, which is why this is a copy rather than an import.
 *
 * Player names were set at a fixed size and clipped with `truncate`, so a name
 * only a little too wide for its column arrived as "VLADIMIR GUERR…". Which
 * names clip is not knowable up front: it depends on the viewport, and also on
 * whether the condensed webfont has loaded — the system fallback is much wider.
 *
 * So the size is measured rather than guessed. Rendered width scales with font
 * size, so one pass lands within a fraction of a pixel; the effect re-runs on
 * its own result and settles. The inner span is sized to its content, so its
 * `scrollWidth` is the text's natural width whether or not it currently fits —
 * which is what lets the size grow back when the container gets wider.
 *
 * Measuring needs real layout, so under jsdom (every dimension 0) the text
 * simply stays at `maxFontSize`.
 *
 * An ellipsis remains the last resort for a name too long even at
 * `minFontSize`; choosing that floor is what keeps it off the screen.
 */
export function FitText({ text, maxFontSize, minFontSize, className, style }: FitTextProps) {
  const containerRef = useRef<HTMLSpanElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [fontSize, setFontSize] = useState(maxFontSize);

  // Re-fitting reads the size it last applied, which would otherwise make the
  // callback a new function on every pass and restart the observer with it.
  const fontSizeRef = useRef(fontSize);
  fontSizeRef.current = fontSize;

  // What this text at this width has already been through, so a size is never
  // revisited. Real glyphs round to whole pixels, so two adjacent half-pixel
  // sizes can each measure as pointing at the other; without this the component
  // would flip between them for as long as it stayed on screen.
  const attemptedRef = useRef<number[]>([]);
  const measuredForRef = useRef<{ width: number; text: string }>({ width: 0, text });

  const fit = useCallback(() => {
    const available = containerRef.current?.clientWidth ?? 0;
    const rendered = textRef.current?.scrollWidth ?? 0;
    if (!available || !rendered) return;

    const measuredFor = measuredForRef.current;
    if (measuredFor.width !== available || measuredFor.text !== text) {
      measuredForRef.current = { width: available, text };
      attemptedRef.current = [];
    }

    const applied = fontSizeRef.current;
    const widthAtMaxSize = (rendered * maxFontSize) / applied;
    const next =
      widthAtMaxSize <= available
        ? maxFontSize
        : Math.max(minFontSize, Math.floor(((maxFontSize * available) / widthAtMaxSize) * 2) / 2);

    // A quarter of a pixel is below what anyone can see, so stop there.
    if (Math.abs(next - applied) < 0.25) return;
    if (attemptedRef.current.includes(next)) return;

    attemptedRef.current.push(applied);
    setFontSize(next);
  }, [maxFontSize, minFontSize, text]);

  useLayoutEffect(fit, [fit, fontSize]);

  useEffect(() => {
    const container = containerRef.current;
    if (!container || typeof ResizeObserver === 'undefined') return;
    const observer = new ResizeObserver(fit);
    observer.observe(container);
    return () => observer.disconnect();
  }, [fit]);

  // A condensed webfont arriving after first paint changes every width on the
  // card, and it is the slow-font case that clipped these names.
  useEffect(() => {
    const fonts = document.fonts;
    if (!fonts?.ready) return;
    let cancelled = false;
    void fonts.ready.then(() => {
      if (!cancelled) fit();
    });
    return () => {
      cancelled = true;
    };
  }, [fit]);

  return (
    <span ref={containerRef} className={`block min-w-0 ${className ?? ''}`} style={style}>
      <span
        ref={textRef}
        className="inline-block max-w-full overflow-hidden text-ellipsis align-top whitespace-nowrap"
        style={{ fontSize }}
      >
        {text}
      </span>
    </span>
  );
}
